import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Paper, Typography, Grid, TextField, Button, FormControl, InputLabel, Select, MenuItem, Divider } from '@mui/material';
import reportsService from '../../../services/reportsService';
import ticketService from '../../../services/ticketService';
import DataTable from '../../../components/design/DataTable';
import LoadingState from '../../../components/design/LoadingState';
import EmptyState from '../../../components/design/EmptyState';

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

export default function ReportsPage() {
  const mountedRef = useRef(true);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [queueId, setQueueId] = useState('all');
  const [status, setStatus] = useState('all');
  const [priority, setPriority] = useState('all');
  const [loading, setLoading] = useState(false);
  const [queues, setQueues] = useState([{ id: 'all', name: 'Todas' }]);
  const [data, setData] = useState(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const params = useMemo(() => {
    const p = {};
    if (from) p.from = from;
    if (to) p.to = to;
    if (queueId !== 'all') p.queueId = queueId;
    if (status !== 'all') p.status = status;
    if (priority !== 'all') p.priority = priority;
    return p;
  }, [from, to, queueId, status, priority]);

  useEffect(() => {
    ticketService.getQueues()
      .then((q) => {
        if (!mountedRef.current) return;
        setQueues([{ id: 'all', name: 'Todas' }, ...(q || [])]);
      })
      .catch(() => {
        if (!mountedRef.current) return;
        setQueues([{ id: 'all', name: 'Todas' }]);
      });
  }, []);

  const load = useCallback(async () => {
    if (mountedRef.current) setLoading(true);
    try {
      const resp = await reportsService.getOverview(params);
      if (mountedRef.current) setData(resp?.data?.data || resp?.data || resp);
    } catch {
      if (mountedRef.current) setData(null);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [params]);

  useEffect(() => { load().catch(() => {}); }, [load]);

  return (
    <Box p={3}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>Relatórios</Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <TextField label="De" type="date" fullWidth InputLabelProps={{ shrink: true }} value={from} onChange={(e) => setFrom(e.target.value)} />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField label="Até" type="date" fullWidth InputLabelProps={{ shrink: true }} value={to} onChange={(e) => setTo(e.target.value)} />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Fila</InputLabel>
              <Select label="Fila" value={queueId} onChange={(e) => setQueueId(e.target.value)}>
                {queues.map(q => (<MenuItem key={q.id} value={q.id}>{q.name}</MenuItem>))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value="new">Novo</MenuItem>
                <MenuItem value="triage">Triagem</MenuItem>
                <MenuItem value="queued">Fila</MenuItem>
                <MenuItem value="assigned">Atribuído</MenuItem>
                <MenuItem value="pending_customer">Pendente cliente</MenuItem>
                <MenuItem value="pending_internal">Pendente interno</MenuItem>
                <MenuItem value="resolved">Resolvido</MenuItem>
                <MenuItem value="closed">Fechado</MenuItem>
                <MenuItem value="reopened">Reaberto</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Prioridade</InputLabel>
              <Select label="Prioridade" value={priority} onChange={(e) => setPriority(e.target.value)}>
                <MenuItem value="all">Todas</MenuItem>
                <MenuItem value="low">Baixa</MenuItem>
                <MenuItem value="medium">Média</MenuItem>
                <MenuItem value="high">Alta</MenuItem>
                <MenuItem value="urgent">Urgente</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={9} display="flex" alignItems="center" gap={1}>
            <Button variant="contained" onClick={load} disabled={loading}>Atualizar</Button>
            <Button
              variant="outlined"
              onClick={async () => {
                const blob = await reportsService.exportAuditCsv(params);
                downloadBlob(blob, 'audit.csv');
              }}
            >
              Exportar auditoria (CSV)
            </Button>
            <Button
              variant="outlined"
              onClick={async () => {
                const blob = await reportsService.exportPausesCsv(params);
                downloadBlob(blob, 'pauses.csv');
              }}
            >
              Exportar pausas (CSV)
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {!data && <LoadingState />}
      {data && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2">Tickets</Typography>
              <Typography variant="h4">{data?.counters?.total ?? 0}</Typography>
              <Divider sx={{ my: 1 }} />
              <Typography variant="body2">Abertos: {data?.counters?.open_like ?? 0}</Typography>
              <Typography variant="body2">Pendentes: {data?.counters?.pending_like ?? 0}</Typography>
              <Typography variant="body2">Fechados: {data?.counters?.closed_like ?? 0}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2">Mensagens</Typography>
              <Typography variant="body2">Falhas de envio: {data?.sendFailures ?? 0}</Typography>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2">Picos por hora</Typography>
              {data?.peakHours?.length ? (
                <DataTable
                  columns={[
                    { key: 'hour', label: 'Hora' },
                    { key: 'total', label: 'Total', align: 'right' }
                  ]}
                  rows={data.peakHours.map(r => ({ ...r, hour: `${String(r.hour).padStart(2, '0')}:00` }))}
                  getKey={(r) => r.hour}
                />
              ) : <EmptyState title="Sem dados" />}
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2">SLA</Typography>
              <Typography variant="body2">Vencidos por fila</Typography>
              {data?.overdueSlaByQueue?.length ? (
                <DataTable
                  columns={[
                    { key: 'queue_name', label: 'Fila' },
                    { key: 'overdue', label: 'Vencidos', align: 'right' }
                  ]}
                  rows={data.overdueSlaByQueue}
                  getKey={(r) => r.queue_id}
                />
              ) : <EmptyState title="Sem vencidos" />}
            </Paper>
          </Grid>
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2">Pausas</Typography>
              <Grid container spacing={2} sx={{ mt: 0 }}>
                <Grid item xs={12} md={4}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="body2" color="text.secondary">Quantidade</Typography>
                    <Typography variant="h5">{data?.pauses?.totals?.total_pauses ?? 0}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Tempo total (s): {data?.pauses?.totals?.total_paused_seconds ?? 0}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" sx={{ mb: 1 }}>Por tipo</Typography>
                  {data?.pauses?.byType?.length ? (
                    <DataTable
                      columns={[
                        { key: 'pause_type_name', label: 'Tipo' },
                        { key: 'total', label: 'Qtd', align: 'right' },
                        { key: 'total_seconds', label: 'Tempo (s)', align: 'right' }
                      ]}
                      rows={data.pauses.byType}
                      getKey={(r) => r.pause_type_id}
                    />
                  ) : <EmptyState title="Sem pausas" />}
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" sx={{ mb: 1 }}>Por operador</Typography>
                  {data?.pauses?.byOperator?.length ? (
                    <DataTable
                      columns={[
                        { key: 'user_name', label: 'Operador' },
                        { key: 'total', label: 'Qtd', align: 'right' },
                        { key: 'total_seconds', label: 'Tempo (s)', align: 'right' }
                      ]}
                      rows={data.pauses.byOperator}
                      getKey={(r) => r.user_id}
                    />
                  ) : <EmptyState title="Sem dados" />}
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      )}

      {data && (
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2">Backlog por fila</Typography>
              {data?.backlogByQueue?.length ? (
                <DataTable
                  columns={[
                    { key: 'queue_name', label: 'Fila' },
                    { key: 'backlog', label: 'Backlog', align: 'right' }
                  ]}
                  rows={data.backlogByQueue}
                  getKey={(r) => r.queue_id}
                />
              ) : <EmptyState title="Sem backlog" />}
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2">Ranking de operadores (fechados)</Typography>
              {data?.topOperators?.length ? (
                <DataTable
                  columns={[
                    { key: 'user_name', label: 'Operador' },
                    { key: 'closed_tickets', label: 'Fechados', align: 'right' },
                    { key: 'avg_resolution_seconds', label: 'Média resolução (s)', align: 'right' }
                  ]}
                  rows={data.topOperators}
                  getKey={(r) => r.user_id}
                />
              ) : <EmptyState title="Sem dados" />}
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}
